import * as LocalAuthentication from 'expo-local-authentication';
import { Platform } from 'react-native';

import { analytics } from '../utils/analytics';

import {
  postBiometricGrant,
  postBiometricRevoke,
  BiometricFreshAuthRequiredError,
} from './api';
import { mapExpoAuthError, type BiometricPromptErrorReason } from './biometricPromptErrors';
import {
  clearAllBiometricState,
  clearBiometricDeclined,
  markBiometricDeclined,
  readBiometricGrant,
  readBiometricUserEmail,
  setBiometricEnrolled,
  storeBiometricGrant,
  storeBiometricUserEmail,
} from './biometricStorage';
import { getOrCreateDeviceId } from './deviceId';

const deviceLabel = (): string =>
  Platform.OS === 'ios' ? 'iOS device' : 'Android device';

const platformForApi = (): 'ios' | 'android' | null => {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return null;
};

export type EnrollResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | BiometricPromptErrorReason
        | 'unsupported_platform'
        | 'fresh_auth_required'
        | 'grant_failed';
    };

// Requires a FRESH access token (login/SSO/magic-link/session-exchange).
// Refresh- or exchange-derived tokens get 403 from Apigee's freshness gate.
// promptMessage/cancelLabel come from the caller (React layer) so the native
// biometric sheet respects the user's Transifex locale.
export const enrollBiometric = async (args: {
  accessToken: string;
  email: string;
  promptMessage: string;
  cancelLabel: string;
}): Promise<EnrollResult> => {
  const platform = platformForApi();
  if (!platform) {
    return { ok: false, reason: 'unsupported_platform' };
  }

  const promptResult = await LocalAuthentication.authenticateAsync({
    promptMessage: args.promptMessage,
    cancelLabel: args.cancelLabel,
    disableDeviceFallback: false,
  });

  if (!promptResult.success) {
    const reason = mapExpoAuthError(promptResult.error);
    analytics.trackEvent('biometric_enrollment_failed', { reason });
    return { ok: false, reason };
  }

  try {
    const deviceId = await getOrCreateDeviceId();
    const grant = await postBiometricGrant({
      accessToken: args.accessToken,
      deviceId,
      deviceName: deviceLabel(),
      platform,
    });

    await storeBiometricGrant(grant.grant_token);
    await storeBiometricUserEmail(args.email);
    await setBiometricEnrolled(true);
    await clearBiometricDeclined();

    analytics.trackEvent('biometric_enrollment_enabled', { platform });
    return { ok: true };
  } catch (err) {
    if (err instanceof BiometricFreshAuthRequiredError) {
      analytics.trackEvent('biometric_enrollment_failed', {
        reason: 'fresh_auth_required',
      });
      await clearAllBiometricState();
      return { ok: false, reason: 'fresh_auth_required' };
    }
    analytics.trackEvent('biometric_enrollment_failed', {
      reason: 'grant_failed',
    });
    await clearAllBiometricState();
    return { ok: false, reason: 'grant_failed' };
  }
};

export const declineBiometricEnrollment = async (): Promise<void> => {
  await markBiometricDeclined();
  analytics.trackEvent('biometric_enrollment_declined');
};

// Revokes server-side then wipes local state — grant, enrolled flag,
// stored email. Local wipe fires even if the server-side call fails so
// the user ends up disabled either way; grant will eventually expire (90d)
// or hit reuse detection.
//
// Preserves the declined latch. Revoke reads as "user said no on this
// device"; the post-login modal stays suppressed so we don't nag. The
// durable in-app path back to enrollment is the login-form opt-in
// checkbox that surfaces for the (declined || revoked) cohort.
export const revokeBiometricEnrollment = async (): Promise<{ ok: boolean }> => {
  let grant: string | null = null;
  try {
    grant = await readBiometricGrant();
  } catch {
    grant = null;
  }
  if (grant) {
    try {
      await postBiometricRevoke({ grant });
    } catch {
      /* best-effort */
    }
  }
  await clearAllBiometricState();
  await markBiometricDeclined();
  analytics.trackEvent('biometric_revoked');
  return { ok: true };
};

// Runs on the login critical path (storeTokens) — must not reject and must
// not block. Fire the server-side revoke without awaiting; local wipes
// swallow SecureStore failures.
export const wipeBiometricStateIfUserSwitched = async (
  newUserEmail: string | null
): Promise<void> => {
  if (!newUserEmail) return;
  let grant: string | null = null;
  try {
    grant = await readBiometricGrant();
  } catch {
    grant = null;
  }
  if (!grant) return;
  let storedEmail: string | null = null;
  try {
    storedEmail = await readBiometricUserEmail();
  } catch {
    storedEmail = null;
  }
  // Django canonicalizes emails to lowercase; a client may have typed a
  // different case at enrollment or at this login.
  if (storedEmail && storedEmail.toLowerCase() === newUserEmail.toLowerCase()) return;

  void postBiometricRevoke({ grant }).catch(() => {
    /* best-effort — grant will TTL on server, and it's already wiped locally */
  });
  try {
    await clearAllBiometricState();
  } catch {
    /* best-effort */
  }
  try {
    await clearBiometricDeclined();
  } catch {
    /* best-effort */
  }
  analytics.trackEvent('biometric_wiped_user_switch');
};
